import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { DatePickerField } from '@/components/ui/date-picker-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Client } from '@/lib/types';
import { createClient, updateClient } from '@/components/clients/services/clients-api';
import { ClientClassificationBadges } from '@/components/clients/ClientClassificationBadges';
import { useToast } from '@/hooks/use-toast';
import { useClientsCacheOptional, type CachedPostureAnalysis } from '@/context/clients-cache-context';
import { useSettings } from '@/components/settings/lib/settings-context';
import {
  convertHeightForDisplay,
  convertHeightToMetric,
  convertWeightForDisplay,
  convertWeightToMetric,
  type Unit,
} from '@/components/clients/lib/unit-conversion';
import { safeToDate, cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { PAIN_SCALE_NRS10, normalizePainLevelNrs } from '@/lib/clients/roster-fields';
import { PainLevelNrsSliderField } from '@/components/clients/fields/PainLevelNrsSliderField';
import logger from '@/lib/logging';

const SEX_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'] as const;

/** Fields that live on Step 1 ("Basic") of the mobile wizard — validated before advancing. */
const STEP_1_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'customId',
  'dateOfBirth',
  'sex',
  'height',
  'weight',
] as const;

const EXPERIENCE_LEVELS = ['Beginner Lifter', 'Novice Lifter', 'Intermediate Lifter', 'Athlete'] as const;

const rosterFormSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required.' }),
  lastName: z.string().optional(),
  email: z.preprocess(
    (val: unknown) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().email({ message: 'Invalid email address.' }).optional()
  ),
  customId: z.string().optional(),
  dateOfBirth: z.date().optional(),
  sex: z.enum(SEX_OPTIONS).optional(),
  // Core biometrics — height is required (it drives every biomechanics calculation),
  // mirroring the legacy /clients/new form this drawer replaced. Entered in the
  // practitioner's display unit; converted to metric (cm/kg) on submit.
  height: z.coerce.number().positive({ message: 'Height is required.' }),
  weight: z.preprocess(
    (val: unknown) => (val === '' || val == null ? undefined : val),
    z.coerce.number().positive({ message: 'Weight must be a positive number.' }).optional()
  ),
  goals: z.string().optional(),
  medicalHistory: z.string().optional(),
  injuryHistory: z.string().optional(),
  liftingExperience: z.number().min(0).max(3).optional(),
  // Phase 6.3 — clinical 0–10 Numeric Rating Scale (NRS; 0 = pain-free).
  painLevel: z.number().min(0).max(10).optional(),
});

type RosterFormValues = z.infer<typeof rosterFormSchema>;

interface ClientFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  allPostureAnalyses?: CachedPostureAnalysis[];
  onSaved?: (saved: Client) => void;
}

function toFormDefaults(client: Client | null | undefined, unit: Unit): RosterFormValues {
  if (!client) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      customId: '',
      dateOfBirth: undefined,
      sex: undefined,
      height: '' as unknown as number,
      weight: '' as unknown as number,
      goals: '',
      medicalHistory: '',
      injuryHistory: '',
      liftingExperience: 0,
      // "Never recorded" — not the same claim as "pain-free" (NRS 0). See
      // normalizePainLevelNrs doc comment.
      painLevel: undefined,
    };
  }

  return {
    firstName: client.firstName ?? (client.name ?? '').split(' ')[0] ?? '',
    lastName: client.lastName ?? (client.name ?? '').split(' ').slice(1).join(' '),
    email: client.email ?? '',
    customId: client.customId ?? '',
    dateOfBirth: client.dateOfBirth ? safeToDate(client.dateOfBirth) ?? undefined : undefined,
    sex: client.sex,
    // Stored metric (cm/kg) → practitioner's display unit for editing.
    height: client.height
      ? convertHeightForDisplay(client.height, unit)
      : ('' as unknown as number),
    weight: client.weight
      ? convertWeightForDisplay(client.weight, unit)
      : ('' as unknown as number),
    goals: client.goals ?? '',
    medicalHistory: client.medicalHistory ?? '',
    injuryHistory: client.injuryHistory ?? '',
    liftingExperience: client.liftingExperience ?? 0,
    // Phase 6.3 — always show the normalized 0–10 NRS value, never the raw
    // stored number (a legacy 0–2 doc would otherwise render at the wrong
    // point on the new slider). May be `undefined` — "never recorded" must
    // never be defaulted to 0 ("pain-free"); see normalizePainLevelNrs doc.
    painLevel: normalizePainLevelNrs(client),
  };
}

export function ClientFormSheet({
  open,
  onOpenChange,
  client,
  allPostureAnalyses,
  onSaved,
}: ClientFormSheetProps) {
  const { toast } = useToast();
  const cache = useClientsCacheOptional();
  const { settings } = useSettings();
  const unit: Unit = settings.unit === 'inches' ? 'inches' : 'cm';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEdit = Boolean(client?.id);
  const isMobile = useIsMobile();

  // Two-step wizard on mobile (>5 fields — ui-ux §4): Step 1 "Basic" (names, email,
  // customId, sex, DOB, height/weight biometrics). Step 2 "Medical/Goals"
  // (goals/medical/injury/experience/pain). Desktop shows every field on one page.
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const showStep1 = !isMobile || step === 1;
  const showStep2 = !isMobile || step === 2;

  const form = useForm<RosterFormValues>({
    resolver: zodResolver(rosterFormSchema) as any,
    defaultValues: toFormDefaults(client, unit),
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormDefaults(client, unit));
      setStep(1);
    }
  }, [open, client, form, unit]);

  /**
   * Unsaved-changes guard: every dismissal path (overlay click, Escape, the Sheet's
   * own X close button, and our Cancel button) funnels through the Sheet Root's
   * `onOpenChange` — intercepting it here catches all of them in one place. When the
   * form is dirty, hold the sheet open and ask for confirmation instead of closing.
   */
  function handleOpenChange(next: boolean) {
    if (!next && form.formState.isDirty && !isSubmitting) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(next);
  }

  function discardAndClose() {
    setConfirmDiscardOpen(false);
    onOpenChange(false);
  }

  async function handleNextStep() {
    const valid = await form.trigger(STEP_1_FIELDS);
    if (valid) setStep(2);
  }

  async function onSubmit(values: RosterFormValues) {
    setIsSubmitting(true);
    const payload: Record<string, unknown> = {
      firstName: values.firstName.trim(),
      // Empty string, NOT undefined: JSON.stringify drops undefined keys, and the
      // server only syncs the computed `name` when BOTH name keys are present in
      // the payload — a dropped lastName left first-name-only clients with no
      // `name` at all, which crashed the Client 360 render.
      lastName: values.lastName?.trim() ?? '',
      email: values.email?.trim() || undefined,
      customId: values.customId?.trim() || undefined,
      dateOfBirth: values.dateOfBirth,
      sex: values.sex,
      // Biometrics — entered in the practitioner's display unit, stored metric (cm/kg).
      height: convertHeightToMetric(values.height, unit),
      weight: values.weight != null ? convertWeightToMetric(values.weight, unit) : undefined,
      goals: values.goals?.trim() || undefined,
      medicalHistory: values.medicalHistory?.trim() || undefined,
      injuryHistory: values.injuryHistory?.trim() || undefined,
      liftingExperience: values.liftingExperience ?? 0,
    };

    // Phase 6.3 — "never recorded" must not be silently promoted into an
    // affirmative 0/10 (pain-free) claim: only write painLevel/painScale when
    // the slider was actually touched; otherwise omit BOTH keys entirely.
    if (values.painLevel !== undefined) {
      payload.painLevel = values.painLevel;
      payload.painScale = PAIN_SCALE_NRS10;
    }

    try {
      if (isEdit && client) {
        await updateClient(client.id, payload as Partial<Client>);
        toast({ title: 'Client updated', description: 'Changes saved successfully.' });
        // updateClient returns void — best-effort merge of the write payload
        // over the pre-edit client so the caller gets a full, id-correct
        // Client without a round-trip fetch.
        onSaved?.({ ...client, ...payload, id: client.id } as Client);
      } else {
        const clientId = await createClient(payload as Omit<Client, 'id'>);
        toast({ title: 'Client created', description: 'Added to your roster.' });
        // createClient returns only the new id — construct the saved Client
        // best-effort from the write payload.
        onSaved?.({ ...payload, id: clientId } as Client);
      }
      cache?.refresh();
      onOpenChange(false);
    } catch (error) {
      logger.error('Client form save failed', error);
      toast({
        title: 'Error',
        description: 'Could not save client. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="client-form-sheet">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit client' : 'Add client'}</SheetTitle>
          <SheetDescription>
            Demographics, goals &amp; medical notes — classification tags come from assessments.
          </SheetDescription>
        </SheetHeader>

        {/* Step indicator — mobile wizard only. */}
        {isMobile && (
          <div className="mt-3 flex items-center gap-2" data-testid="client-form-step-indicator">
            <div className="flex items-center gap-1.5">
              {[1, 2].map((s) => (
                <span
                  key={s}
                  className={cn(
                    'h-1.5 w-6 rounded-full transition-colors',
                    s === step ? 'bg-primary' : 'bg-muted'
                  )}
                  aria-hidden
                />
              ))}
            </div>
            <p className="text-xs font-medium text-muted-foreground">
              Step {step} of 2 — {step === 1 ? 'Basic info' : 'Medical & goals'}
            </p>
          </div>
        )}

        {showStep1 && isEdit && client && (
          <div className="mt-4 space-y-2 rounded-md border p-3 bg-muted/30">
            <p className="text-xs font-medium text-muted-foreground uppercase">Classification (read-only)</p>
            <ClientClassificationBadges
              client={client}
              allPostureAnalyses={allPostureAnalyses}
            />
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
            {showStep1 && (
              <>
            {/* Single column on mobile — never side-by-side (ui-ux §4). */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="client-first-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="client-last-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} data-testid="client-email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom ID</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional external ID" data-testid="client-custom-id" />
                  </FormControl>
                  <FormDescription>
                    Optional external reference ID (e.g. EMR/chart number).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of birth</FormLabel>
                  <FormControl>
                    <DatePickerField value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sex"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sex</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="client-sex" className="h-12 md:h-10">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SEX_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Core biometrics — height drives every biomechanics calculation, so it
                lives on step 1 with the identity fields, not buried in Medical/Goals. */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Height ({unit === 'inches' ? 'in' : 'cm'})</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        inputMode="decimal"
                        placeholder={unit === 'inches' ? 'e.g. 74' : 'e.g. 188'}
                        data-testid="client-height"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight ({unit === 'inches' ? 'lbs' : 'kg'})</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        inputMode="decimal"
                        placeholder={unit === 'inches' ? 'e.g. 225' : 'e.g. 102'}
                        data-testid="client-weight"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
              </>
            )}

            {showStep2 && (
            <div className="space-y-4 rounded-md border p-3">
              <p className="text-xs font-medium uppercase text-muted-foreground">Medical &amp; Goals</p>

              <FormField
                control={form.control}
                name="goals"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Goals</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="What are the client's primary training goals?"
                        className="min-h-[80px]"
                        data-testid="client-goals"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="medicalHistory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Medical history</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Client's medical history…"
                        className="min-h-[80px]"
                        data-testid="client-medical-history"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="injuryHistory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Injury history</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Note any relevant past or current injuries…"
                        className="min-h-[80px]"
                        data-testid="client-injury-history"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="liftingExperience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Lifting experience:{' '}
                      <span className="font-semibold text-primary">
                        {EXPERIENCE_LEVELS[field.value ?? 0]}
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Slider
                        min={0}
                        max={3}
                        step={1}
                        value={[field.value ?? 0]}
                        valueText={EXPERIENCE_LEVELS[field.value ?? 0]}
                        aria-label="Lifting experience"
                        onValueChange={(value) => field.onChange(value[0])}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="painLevel"
                render={({ field }) => (
                  <PainLevelNrsSliderField
                    value={field.value}
                    onChange={field.onChange}
                    testId="client-pain-level"
                  />
                )}
              />
            </div>
            )}

            <SheetFooter className="pt-2">
              {isMobile && step === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 md:h-10"
                  onClick={() => setStep(1)}
                  data-testid="client-form-back"
                >
                  Back
                </Button>
              )}
              {(!isMobile || step === 2) && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 md:h-10"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
              )}
              {isMobile && step === 1 && (
                <Button
                  type="button"
                  className="h-12 md:h-10"
                  onClick={() => void handleNextStep()}
                  data-testid="client-form-next"
                >
                  Next
                </Button>
              )}
              {(!isMobile || step === 2) && (
                <Button type="submit" className="h-12 md:h-10" disabled={isSubmitting} data-testid="client-form-save">
                  {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create client'}
                </Button>
              )}
            </SheetFooter>
          </form>
        </Form>

        <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes to this client. Closing now will discard them.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction onClick={discardAndClose} className="bg-destructive hover:bg-destructive/90">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
